import type { DealStage, PipelineType } from "@prisma/client";
import type {
  DealDisqualificationReason,
  DealLostReason,
} from "@prisma/client";

/**
 * Canonical seller pipeline stages (ordered).
 * Lead → qualify → NDA → data room → listing agreement → market → close.
 */
export const SELLER_DEAL_STAGES = [
  "LEAD",
  "QUALIFYING",
  "QUALIFIED",
  "NDA_SENT",
  "DISCOVERY",
  "LISTING_AGREEMENT",
  "LISTING_PREP",
  "LIVE_ON_MARKET",
  "REVIEWING_OFFERS",
  "UNDER_CONTRACT",
  "CLOSED_WON",
  "CLOSED_LOST",
] as const satisfies readonly DealStage[];

/** Canonical buyer pipeline stages (ordered). */
export const BUYER_DEAL_STAGES = [
  "NEW_INQUIRY",
  "QUALIFIED",
  "NDA_SENT",
  "PREQUALIFICATION",
  "PREQUALIFIED",
  "LOI",
  "PURCHASE_AGREEMENT",
  "UNDER_CONTRACT",
  "CLOSED_WON",
  "CLOSED_LOST",
] as const satisfies readonly DealStage[];

export type SellerDealStage = (typeof SELLER_DEAL_STAGES)[number];
export type BuyerDealStage = (typeof BUYER_DEAL_STAGES)[number];

/** The listing can only be published after the listing agreement is signed. */
export const LISTING_PUBLISH_MIN_SELLER_STAGE = "LISTING_PREP";

export function canPublishListingAtStage(
  stage: DealStage | string | null | undefined,
): boolean {
  if (stage === "CLOSED_LOST") return false;
  const stageIndex = SELLER_DEAL_STAGES.indexOf(stage as SellerDealStage);
  const minimumIndex = SELLER_DEAL_STAGES.indexOf(
    LISTING_PUBLISH_MIN_SELLER_STAGE,
  );
  return stageIndex >= minimumIndex;
}

export const SELLER_DEAL_STAGE_LABELS = {
  LEAD: "New Lead",
  QUALIFIED: "Qualified",
  QUALIFYING: "Qualifying",
  NDA_SENT: "NDA",
  DISCOVERY: "Data gathering",
  LISTING_AGREEMENT: "Listing agreement",
  LISTING_PREP: "Listing prep",
  LIVE_ON_MARKET: "Live on market",
  REVIEWING_OFFERS: "Reviewing offers",
  UNDER_CONTRACT: "Under contract",
  CLOSED_WON: "Sold",
  CLOSED_LOST: "Lost",
} as const satisfies Record<SellerDealStage, string>;

export const BUYER_DEAL_STAGE_LABELS = {
  NEW_INQUIRY: "New Inquiry",
  QUALIFIED: "Qualified",
  NDA_SENT: "NDA",
  PREQUALIFICATION: "Prequalification",
  PREQUALIFIED: "Reviewing businesses",
  LOI: "LOI",
  PURCHASE_AGREEMENT: "Purchase agreement",
  UNDER_CONTRACT: "Under contract",
  CLOSED_WON: "Won",
  CLOSED_LOST: "Lost",
} as const satisfies Record<BuyerDealStage, string>;

export const DEAL_STAGE_LABELS: Record<string, string> = {
  ...BUYER_DEAL_STAGE_LABELS,
  ...SELLER_DEAL_STAGE_LABELS,
};

export const DEAL_DISQUALIFICATION_REASON_LABELS: Record<
  DealDisqualificationReason,
  string
> = {
  NOT_BUSINESS_OWNER: "Not the business owner",
  NOT_READY_TO_SELL: "Not ready to sell",
  QUALIFIED_NOT_READY: "Good lead, not ready yet",
  BUSINESS_TOO_SMALL: "Business is too small",
  OUTSIDE_TERRITORY: "Outside territory",
  SPAM_OR_FAKE: "Spam or fake inquiry",
  UNRESPONSIVE: "Unresponsive",
  PRICE_EXPECTATIONS: "Price expectations did not align",
  OTHER: "Other",
};

/** Ordered for Mark unqualified in OS and campaign lead follow-up email buttons. */
export const DEAL_DISQUALIFICATION_REASONS = [
  "NOT_BUSINESS_OWNER",
  "NOT_READY_TO_SELL",
  "QUALIFIED_NOT_READY",
  "BUSINESS_TOO_SMALL",
  "OUTSIDE_TERRITORY",
  "SPAM_OR_FAKE",
  "UNRESPONSIVE",
  "PRICE_EXPECTATIONS",
  "OTHER",
] as const satisfies readonly DealDisqualificationReason[];

export function dealDisqualificationReasonOptions(
  pipelineType: PipelineType | string = "SELLER",
): Array<{
  reason: DealDisqualificationReason;
  label: string;
}> {
  return DEAL_DISQUALIFICATION_REASONS.filter(
    (reason) =>
      pipelineType !== "BUYER" ||
      ![
        "NOT_BUSINESS_OWNER",
        "NOT_READY_TO_SELL",
        "QUALIFIED_NOT_READY",
        "BUSINESS_TOO_SMALL",
      ].includes(reason),
  ).map((reason) => ({
    reason,
    label: DEAL_DISQUALIFICATION_REASON_LABELS[reason],
  }));
}

/** Ordered for display in the one-click "Not interested" email flow and the OS. */
export const DEAL_LOST_REASONS = [
  "NOT_A_FIT",
  "OUTSIDE_TERRITORY",
  "TOO_SMALL",
  "ALREADY_ENGAGED_ELSEWHERE",
  "UNRESPONSIVE",
  "OTHER",
] as const satisfies readonly DealLostReason[];

export const DEAL_LOST_REASON_LABELS: Record<DealLostReason, string> = {
  NOT_A_FIT: "Not a fit for my practice",
  OUTSIDE_TERRITORY: "Outside my area",
  TOO_SMALL: "Too small",
  ALREADY_ENGAGED_ELSEWHERE: "Already working with another broker",
  UNRESPONSIVE: "Could not reach them",
  OTHER: "Other",
};

/** Seller stages that represent a listing already being worked in-market. */
export const MARKET_SELLER_DEAL_STAGES = [
  "LIVE_ON_MARKET",
  "REVIEWING_OFFERS",
  "UNDER_CONTRACT",
  "CLOSED_WON",
] as const satisfies readonly SellerDealStage[];

/** Canonical projection from listing lifecycle to seller workspace stage. */
export const LISTING_STATUS_TO_SELLER_STAGE: Partial<
  Record<string, SellerDealStage>
> = {
  FOR_SALE: "LIVE_ON_MARKET",
  REVIEWING_OFFERS: "REVIEWING_OFFERS",
  UNDER_CONTRACT: "UNDER_CONTRACT",
  SOLD: "CLOSED_WON",
};

export function formatDealStageLabel(stage: string): string {
  return (
    DEAL_STAGE_LABELS[stage] ??
    stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function getDealStagesForPipeline(
  pipelineType: PipelineType,
): readonly DealStage[] {
  return pipelineType === "SELLER" ? SELLER_DEAL_STAGES : BUYER_DEAL_STAGES;
}

export function isSellerDealStage(stage: DealStage): stage is SellerDealStage {
  return (SELLER_DEAL_STAGES as readonly DealStage[]).includes(stage);
}

export function isBuyerDealStage(stage: DealStage): stage is BuyerDealStage {
  return (BUYER_DEAL_STAGES as readonly DealStage[]).includes(stage);
}

export function buyerStageIndex(stage: DealStage | null | undefined): number {
  if (!stage) return -1;
  return (BUYER_DEAL_STAGES as readonly DealStage[]).indexOf(stage);
}

/** Furthest buyer milestone; never regresses. */
export function highestBuyerMilestone(
  current: DealStage | null | undefined,
  candidate: DealStage,
): DealStage {
  const currentIdx = buyerStageIndex(current);
  const candidateIdx = buyerStageIndex(candidate);
  if (candidateIdx < 0) return current ?? candidate;
  if (currentIdx < 0) return candidate;
  return candidateIdx > currentIdx ? candidate : (current as DealStage);
}
